import React, { useState, ChangeEvent, FormEvent, useCallback } from "react";
import {
  getPhoneValidationMessage as validatePhoneNumber,
  PHONE_INPUT_MAX_DIGITS,
} from "../utils/phoneValidation";
import {
  Drawer,
  Box,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import themestyle from "../theme";
import {
  MSG,
  INPUT_REGEX,
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  MESSAGES,
} from "../utils/const";
import { countries, getEmojiFlag, TCountryCode } from "countries-list";
import * as Flags from "country-flag-icons/react/3x2";
import {
  Autocomplete,
} from "@mui/material";
import { useMutation } from "@apollo/client/react";
import { CREATE_ADMIN } from "../graphql/mutation";
import {
  CreateAdminResponse,
  CreateAdminInput,
} from "../interfaces/auth.interface";
import {
  validateAdminField,
} from "../utils/validation";


type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (message: string) => void;
};

const AdminDrawer: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  onError,
}) => {
  const DEFAULT_COUNTRY = "India";
  const countryOptions = Object.entries(countries)
    .map(([code, country]) => ({
      code: code as TCountryCode,
      name: country.name,
      emoji: getEmojiFlag(code as TCountryCode),
      dialCode: String(country.phone?.[0] || ""),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const getCountryDialCode = (countryName: string) => {
    const matchedCountry = countryOptions.find((country) => country.name === countryName);
    return matchedCountry?.dialCode || "";
  };

  // Phone is required here; the shared libphonenumber-js util does the accurate
  // per-country length/format check once the field is non-empty.
  const getPhoneValidationMessage = (phone: string, countryName: string) => {
    if (!phone.replace(/\D/g, "")) return MSG.PHONE_REQUIRED;
    return validatePhoneNumber(phone, countryName);
  };

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    country: DEFAULT_COUNTRY,
    postcode: "",
  });
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY);
  const [phoneCountryMenuAnchor, setPhoneCountryMenuAnchor] = useState<null | HTMLElement>(null);
  const [phoneCountrySearch, setPhoneCountrySearch] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createAdmin, { loading }] = useMutation<
    CreateAdminResponse,
    { input: CreateAdminInput }
  >(CREATE_ADMIN);
  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      email: "",
      password: "",
      phone: "",
      address: "",
      country: DEFAULT_COUNTRY,
      postcode: "",
    });
    setPhoneCountry(DEFAULT_COUNTRY);
    setPhoneCountryMenuAnchor(null);
    setPhoneCountrySearch("");
    setErrors({});
    setShowPassword(false);
  }, []);
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "name" || name === "country") {
      setFormData((prev) => ({
        ...prev,
        [name]: value.replace(INPUT_REGEX.ONLY_ALPHABETS_SPACE, ""),
      }));
      return;
    }
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, phone: digitsOnly }));
      setErrors((prev) => ({
        ...prev,
        phone: digitsOnly ? getPhoneValidationMessage(digitsOnly, phoneCountry) : "",
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const msg = name === "phone" ? getPhoneValidationMessage(value, phoneCountry) : validateAdminField(name, value);
    setErrors((prev) => ({ ...prev, [name]: msg }));
  };

  const handleCountryChange = (event: any, newValue: { code: string; name: string } | null) => {
    const value = newValue ? newValue.name : "";
    setFormData((prev) => ({
      ...prev,
      country: value,
    }));
    const msg = validateAdminField("country", value);
    setErrors((prev) => ({ ...prev, country: msg }));
  };

  const handlePhoneCountryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPhoneCountryMenuAnchor(event.currentTarget);
  };

  const handlePhoneCountryMenuClose = () => {
    setPhoneCountryMenuAnchor(null);
    setPhoneCountrySearch("");
  };

  const handlePhoneCountryChange = (newValue: { code: string; name: string } | null) => {
    const nextCountry = newValue?.name || DEFAULT_COUNTRY;
    setPhoneCountry(nextCountry);
    setErrors((prev) => ({
      ...prev,
      phone: formData.phone ? getPhoneValidationMessage(formData.phone, nextCountry) : "",
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    try {
      const { data } = await createAdmin({
        variables: {
          input: {
            email: formData.email,
            password: formData.password,
            name: formData.name,
            phoneNumber: formData.phone,
            country: formData.country,
            postcode: formData.postcode,
            address: formData.address,
            role: "ADMIN",
          },
        },
      });
      if (!data?.createAdmin.success) {
        onError?.(data?.createAdmin.message || "Failed to create admin");
        return;
      }
      resetForm();
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
    }
  };
  const handleClose = () => {
    resetForm();
    onClose();
  };
  const filteredPhoneCountryOptions = countryOptions.filter((country) => {
    const query = phoneCountrySearch.trim().toLowerCase();
    if (!query) return true;

    return (
      country.name.toLowerCase().includes(query) ||
      country.dialCode.includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  });

  const selectedPhoneCountry =
    countryOptions.find((country) => country.name === phoneCountry) ||
    countryOptions.find((country) => country.name === DEFAULT_COUNTRY) ||
    null;

  const selectedDialCode = getCountryDialCode(phoneCountry);

  const isFormValid =
    formData.name.length >= 2 &&
    !!formData.email &&
    !!formData.password &&
    !validateAdminField("email", formData.email) &&
    !validateAdminField("password", formData.password) &&
    !getPhoneValidationMessage(formData.phone, phoneCountry) &&
    !validateAdminField("postcode", formData.postcode) &&
    !validateAdminField("country", formData.country) &&
    !validateAdminField("address", formData.address);

  const textFieldSX = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      minHeight: { xs: "38px", sm: "44px" },
      "& input": {
        fontSize: { xs: "11px", sm: "14px" },
        padding: { xs: "12px", sm: "10px 12px" },
        fontFamily: themestyle.fontFamily.Roboto,
      },
      "& textarea": {
        fontSize: { xs: "12px", sm: "14px" },
        padding: "12px",
        fontFamily: themestyle.fontFamily.Roboto,
      },
      "&.Mui-focused fieldset": {
        borderColor: themestyle.colors.activeGreen,
        borderWidth: "1px",
      },
    },
    "& .MuiFormHelperText-root": {
      fontSize: { xs: "9px", sm: "12px" },
      marginLeft: "4px",
      fontFamily: themestyle.fontFamily.Roboto,
    },
  };
  
  const Label = ({ text }: { text: string }) => {
    const hasAsterisk = text.includes("*");
    const labelText = hasAsterisk ? text.replace(/\s*\*$/, "") : text;

    return (
      <Box
        sx={{
          fontSize: { xs: "12px", sm: "15px" },
          fontWeight: 500,
          mb: 0.6,
          fontFamily: themestyle.fontFamily.Roboto,
        }}
      >
        {labelText}
        {hasAsterisk && <Box component="span" sx={{ color: themestyle.colors.error }}> * </Box>}
      </Box>
    );
  };
  
  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box
        sx={{
          width: { xs: "70vw", sm: 400 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ px: 2, pt: { xs: 1, sm: 2 }, }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: { xs: 1, sm: 2 },
              pb: { xs: 0.8, sm: 1.5 },
              borderBottom: `1px solid ${themestyle.colors.border}`,
            }}
          >
            <Box
              sx={{
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "20px" },
                fontFamily: themestyle.fontFamily.Roboto,
              }}
            >
              Add Admin
            </Box>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", px: 2, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none", }, }}>
          <form autoComplete="off">
            <Label text={`${FORM_LABELS.FULL_NAME} *`}/>
            <TextField
              name="name"
              value={formData.name}
              placeholder={FORM_PLACEHOLDERS.NAME}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              sx={textFieldSX}
            />
            <Box mt={2}>
              <Label text={`${FORM_LABELS.EMAIL} *`} />
              <TextField
                name="email"
                autoComplete="off"   
                value={formData.email}
                placeholder={FORM_PLACEHOLDERS.EMAIL}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
                sx={textFieldSX}
              />
            </Box>
            <Box mt={2}>
              <Label text={`${MESSAGES.PASSWORD_LABEL} *`} />
              <TextField
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                placeholder={MESSAGES.PASSWORD_PLACEHOLDER}
                autoComplete="new-password"  
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.password}
                helperText={errors.password}
                fullWidth
                sx={textFieldSX}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ "& svg": { fontSize: { xs: 18, sm: 20, md: 24, } } }}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box mt={2}>
              <Label text={`${FORM_LABELS.PHONE} *`} />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: { xs: "38px", sm: "44px" },
                  border: "1px solid",
                  borderColor: errors.phone ? themestyle.colors.error : "rgba(0, 0, 0, 0.23)",
                  borderRadius: "8px",
                  backgroundColor: themestyle.colors.white,
                  overflow: "hidden",
                  "&:focus-within": {
                    borderColor: themestyle.colors.activeGreen,
                  },
                }}
              >
                <Box
                  onClick={handlePhoneCountryMenuOpen}
                  sx={{
                    width: "fit-content",
                    maxWidth: 120,
                    px: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    borderRight: "1px solid #E4E7EC",
                    cursor: "pointer",
                    flexShrink: 0,
                    height: "100%",
                    fontFamily: themestyle.fontFamily.Roboto,
                  }}
                >
                  {(() => {
                    const Flag = selectedPhoneCountry ? (Flags as any)[selectedPhoneCountry.code] : null;

                    return (
                      <>
                        <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {Flag ? (
                            <Flag style={{ width: 20, height: 14, borderRadius: "2px" }} />
                          ) : (
                            <Typography sx={{ fontSize: 13, fontFamily: themestyle.fontFamily.Roboto }}>
                              {selectedPhoneCountry?.emoji || "🌐"}
                            </Typography>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1D2939", fontFamily: themestyle.fontFamily.Roboto }}>
                          {selectedDialCode ? `+${selectedDialCode}` : "Code"}
                        </Typography>
                        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 17, color: "#98A2B3" }} />
                      </>
                    );
                  })()}
                </Box>

                <TextField
                  name="phone"
                  value={formData.phone}
                  placeholder={FORM_PLACEHOLDERS.PHONE_UK}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={false}
                  fullWidth
                  variant="standard"
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: PHONE_INPUT_MAX_DIGITS,
                  }}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                    height: "100%",
                    "& .MuiInputBase-root": {
                      display: "flex",
                      alignItems: "center",
                      px: 1.5,
                      width: "100%",
                      height: "100%",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "11px", sm: "14px" },
                      color: "#1D2939",
                      padding: 0,
                      height: "100%",
                      boxSizing: "border-box",
                      fontFamily: themestyle.fontFamily.Roboto,
                    },
                  }}
                />
              </Box>
              {errors.phone ? (
                <Box
                  sx={{
                    fontSize: { xs: "9px", sm: "12px" },
                    color: themestyle.colors.error,
                    marginLeft: "4px",
                    marginTop: "4px",
                    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                  }}
                >
                  {errors.phone}
                </Box>
              ) : null}
              <Menu
                anchorEl={phoneCountryMenuAnchor}
                open={Boolean(phoneCountryMenuAnchor)}
                onClose={handlePhoneCountryMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    width: 320,
                    maxHeight: 380,
                    borderRadius: "16px",
                    border: "1px solid #D0D5DD",
                    boxShadow: "0 20px 40px rgba(16, 24, 40, 0.12)",
                    p: 0.5,
                  },
                }}
                MenuListProps={{ disablePadding: true }}
              >
                <Box sx={{ p: 1 }}>
                  <TextField
                    value={phoneCountrySearch}
                    onChange={(event) => setPhoneCountrySearch(event.target.value)}
                    placeholder="Search"
                    fullWidth
                    size="small"
                    autoFocus
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    InputProps={{
                      startAdornment: <SearchOutlinedIcon sx={{ color: "#98A2B3", mr: 1 }} />,
                    }}
                  />
                </Box>
                <Box sx={{ maxHeight: 300, overflowY: "auto", py: 0.5 }}>
                  {filteredPhoneCountryOptions.map((country) => {
                    const Flag = (Flags as any)[country.code];
                    const isActive = country.name === phoneCountry;

                    return (
                      <MenuItem
                        key={country.code}
                        onClick={() => {
                          handlePhoneCountryChange(country);
                          handlePhoneCountryMenuClose();
                        }}
                        sx={{
                          mx: 0.5,
                          mb: 0.25,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          backgroundColor: isActive ? "rgba(30, 127, 61, 0.08)" : "transparent",
                        }}
                      >
                        <Box sx={{ width: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {Flag ? (
                            <Flag style={{ width: 20, height: 14, borderRadius: "2px" }} />
                          ) : (
                            <span>{country.emoji}</span>
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, color: "#1D2939", fontFamily: themestyle.fontFamily.Roboto }}>
                            {country.name}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#667085", fontFamily: themestyle.fontFamily.Roboto }}>
                          +{country.dialCode || "—"}
                        </Typography>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Menu>
            </Box>
            <Box mt={2}>
              <Label text={`${FORM_LABELS.ADDRESS} *`} />
              <TextField
                multiline
                rows={2}
                name="address"
                value={formData.address}
                  placeholder={FORM_LABELS.ADDRESS_FORM}
                onBlur={handleBlur}
                error={!!errors.address}
                onChange={handleChange}
                helperText={errors.address}
                fullWidth
                // sx={textFieldSX}
                sx={{...textFieldSX,
                  "& .MuiOutlinedInput-root textarea": {
                    padding: "4px 6px",
                  },
                }}
              />
            </Box>
            <Box mt={2}>
              <Label text={`${FORM_LABELS.COUNTRY} *`} />
              <Autocomplete
                options={countryOptions}
                getOptionLabel={(option) => option.name}
                value={countryOptions.find((c) => c.name === formData.country) || null}
                onChange={handleCountryChange}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props as any;
                  const Flag = (Flags as any)[option.code];
                  return (
                    <Box 
                      key={key}
                      component="li" 
                      {...optionProps} 
                      sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 1.5, 
                        fontSize: { xs: "12px", sm: "14px" },
                        py: 1
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", width: 24, flexShrink: 0 }}>
                        {Flag ? <Flag style={{ width: "100%", height: "auto", borderRadius: "2px" }} /> : <span>{option.emoji}</span>}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {option.name}
                      </Box>
                    </Box>
                  );
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: "0px !important",
                    paddingLeft: "12px !important",
                    "& .MuiAutocomplete-input": {
                      padding: { xs: "12px !important", sm: "10px 12px !important" },
                      paddingLeft: "8px !important",
                    },
                  },
                }}
                renderInput={(params) => {
                  const selectedCountry = countryOptions.find((c) => c.name === formData.country);
                  const Flag = selectedCountry ? (Flags as any)[selectedCountry.code] : null;
                  
                  return (
                    <TextField
                      {...params}
                      placeholder={FORM_LABELS.COUNTRY}
                      error={!!errors.country}
                      helperText={errors.country}
                      sx={textFieldSX}
                      autoComplete="new-password"
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: selectedCountry && Flag ? (
                          <Box sx={{ display: "flex", alignItems: "center", width: 20, mr: -1, ml: 0.5 }}>
                            <Flag style={{ width: "100%", height: "auto", borderRadius: "1px" }} />
                          </Box>
                        ) : params.InputProps.startAdornment
                      }}
                    />
                  );
                }}
                slotProps={{
                  popper: {
                    placement: "bottom-start",
                    modifiers: [
                      {
                        name: 'flip',
                        enabled: false,
                      },
                    ],
                  },
                  paper: {
                    sx: {
                      mt: 1,
                      borderRadius: "10px",
                      boxShadow: "0px 10px 30px rgba(0,0,0,0.08)",
                      border: "1px solid #F0F0F0",
                      "& .MuiAutocomplete-listbox": {
                        maxHeight: "300px",
                        padding: "6px 0",
                        "& .MuiAutocomplete-option": {
                          padding: "10px 18px",
                          "&[aria-selected='true']": {
                            backgroundColor: "rgba(30, 127, 61, 0.08)",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }
                      }
                    }
                  }
                }}
              />
            </Box>
            <Box mt={2}>
              <Label text={`${FORM_LABELS.POSTCODE} *`} />
              <TextField
                name="postcode"
                value={formData.postcode}
                placeholder={FORM_LABELS.POSTCODE}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.postcode}
                helperText={errors.postcode}
                fullWidth
                sx={textFieldSX}
              />
            </Box>
          </form>
        </Box>
        <Box
          sx={{
            mt: 3,
            px: 2,
            py: { xs: 1.5, sm: 2 },
            borderTop: `1px solid ${themestyle.colors.border}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: { xs: 1, sm: 1.5 },
            backgroundColor: themestyle.colors.white,
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              minWidth: { xs: "70px", sm: "80px" },
              height: { xs: "28px", sm: "35px" },
              borderRadius: "4px",
              backgroundColor: themestyle.colors.white,
              color: themestyle.colors.black,
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 500,
              textTransform: "none",
              border: `1px solid ${themestyle.colors.border}`,

              "&:hover": {
                backgroundColor: "#F5F5F5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid}
            sx={{
              minWidth: { xs: "80px", sm: "90px" },
              height: { xs: "28px", sm: "35px" },
              borderRadius: "4px",
              backgroundColor: themestyle.colors.activeGreen,
              color: themestyle.colors.white,
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: themestyle.colors.secondary,
              },
              "&:disabled": {
                backgroundColor: themestyle.colors.GRAY_DISABLED,
                color: themestyle.colors.white,
              },
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
export default AdminDrawer;
